/**
 * Resolves which deal custom-field column keys are excluded by the workspace's
 * picked summary template (methodology). See methodology-scope.ts for the
 * exclusion rule; this hook supplies it with backend data and turns bare field
 * names into grid column keys (`custom_<fieldName>_<workspaceId>_coffee` — the
 * same crm_name convention the backend uses when materializing the field).
 *
 * All three queries are cache-first and stay fresh without refetchQueries:
 * updateWorkspaceIntelligence returns defaultSummaryTemplateId in its payload
 * and template mutations return full SummaryTemplateFields, so normalized-cache
 * writes re-run the filter live when the pick changes in Settings.
 */

import { useMemo } from 'react';
import {
  useGetWorkspaceIntelligenceQuery,
  useGetWorkspaceSummaryTemplatesQuery,
  useGetDealFieldsQuery,
} from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { isLovablePreview } from '@/lib/utils/environment';
import { computeExcludedFieldNames, type ScopeTemplate } from '@/lib/methodology-scope';

const EMPTY_SET: ReadonlySet<string> = new Set();

const settled = (q: { loading: boolean; data?: unknown; error?: unknown }) =>
  !q.loading && (q.data !== undefined || q.error !== undefined);

export interface MethodologyColumnScope {
  /** Grid column keys to hide, e.g. `custom_budget_893_coffee`. */
  excludedKeys: ReadonlySet<string>;
  /**
   * True once the exclusion set is final: every scope query settled (data or
   * error), or the hook short-circuited (disabled / preview / no workspace).
   * While false, scoped consumers should hold back custom fields rather than
   * flash all methodologies' columns and then remove them.
   */
  resolved: boolean;
}

export function useMethodologyExcludedColumnKeys(enabled: boolean): MethodologyColumnScope {
  const workspaceId = useWorkspaceId();
  const preview = isLovablePreview();
  // Preview has no backend; its fields come from the local preview registry
  // and must never be filtered.
  const skip = !enabled || preview || !workspaceId;

  const intelligence = useGetWorkspaceIntelligenceQuery({
    variables: { workspaceId: workspaceId ?? '' },
    fetchPolicy: 'cache-first',
    skip,
  });
  const templates = useGetWorkspaceSummaryTemplatesQuery({
    variables: { workspaceId: workspaceId ?? '' },
    fetchPolicy: 'cache-first',
    skip,
  });
  const dealFields = useGetDealFieldsQuery({
    variables: { workspaceId: workspaceId ?? '' },
    fetchPolicy: 'cache-first',
    skip,
  });

  const resolved = skip || (settled(intelligence) && settled(templates) && settled(dealFields));

  const excludedKeys = useMemo<ReadonlySet<string>>(() => {
    if (skip || !resolved) return EMPTY_SET;
    // Any errored query → fail open (status quo: show everything).
    if (intelligence.error || templates.error || dealFields.error) return EMPTY_SET;

    // Neither connection is paginated here (no `first`/`pageInfo` — the shared
    // operations are reused as-is so their cache entries dedupe with Settings').
    // A truncated page can only fail OPEN: a missing deal field shrinks the
    // id→name map (that exclusion is skipped), a missing preset shrinks the
    // union, and a missing picked template makes the pick unresolvable (no
    // filtering at all). Over-exclusion is impossible since the picked
    // template's sections travel with the template itself.
    const pickedId = intelligence.data?.workspaceIntelligence?.defaultSummaryTemplateId;
    const templateList: ScopeTemplate[] = (templates.data?.summaryTemplates?.edges ?? [])
      .map(e => e?.node)
      .filter((n): n is NonNullable<typeof n> => n != null);
    const nameById = new Map<string, string>();
    for (const edge of dealFields.data?.dealFields?.edges ?? []) {
      const node = edge?.node;
      if (node?.id && node.fieldName) nameById.set(node.id, node.fieldName);
    }

    const excludedNames = computeExcludedFieldNames(templateList, pickedId, nameById);
    if (excludedNames.size === 0) return EMPTY_SET;
    return new Set(
      [...excludedNames].map(fieldName => `custom_${fieldName}_${workspaceId}_coffee`),
    );
  }, [
    skip,
    resolved,
    intelligence.data,
    intelligence.error,
    templates.data,
    templates.error,
    dealFields.data,
    dealFields.error,
    workspaceId,
  ]);

  return { excludedKeys, resolved };
}
